"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Activity, BookOpen, LoaderCircle, RefreshCw, Search, ServerCog, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ResponsiveStatusRow } from "@/components/runyi-responsive-status-row";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ProviderControlPanel } from "./provider-control-panel";
import { EnterpriseFabricPanel } from "./enterprise-fabric-panel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type FleetTenant = {
  member_email?: string;
  project_ref?: string;
  display_name?: string | null;
  connection_status?: string | null;
  observed_schema_version?: string | null;
  verified_schema_version?: string | null;
  verification_status?: string | null;
  lifecycle_state?: string | null;
  health_status?: string | null;
  active_drift_count?: number;
  active_security_count?: number;
  last_error?: string | null;
};

type FleetPayload = {
  generated_at?: string;
  summary?: Record<string, number>;
  filters?: string[];
  tenants?: FleetTenant[];
};

type KnowledgeItem = {
  knowledge_key: string;
  domain: string;
  title: string;
  summary: string;
  source_type?: string;
  source_ref?: string | null;
  authoritative?: boolean;
  updated_at?: string;
  content?: {
    status?: string;
    domain?: string;
    git_url?: string;
    canonical_commit?: string;
    current_version?: string;
    web_release?: string;
    web_verification?: string;
    control_tower_edge_release?: string;
    control_tower_edge_state?: string;
    edge_function_version?: number;
    runtime_env_canonical_commit?: string;
    authenticated_business_smoke_test?: string;
  };
};

type OwnerRuntimeTest = {
  checkedAt: string;
  state: "VERIFIED" | "PARTIAL" | "FAILED";
  businessCheck: "PASS" | "FAIL";
  runtimeCommitCheck: "VERIFIED" | "UNKNOWN" | "DRIFT";
  architecture: string;
  expectedCommit: string;
  observedCommit: string;
  ownerScope: string;
  summaryCount: number;
  message: string;
};

type ReadinessPayload = {
  ready?: boolean;
  summary?: Record<string, number>;
  checks?: Array<{
    check_key: string;
    category: string;
    description: string;
    required: boolean;
    status: string;
  }>;
};

function statusTone(value?: string | null) {
  const state = String(value ?? "").toUpperCase();
  if (["ACTIVE", "VERIFIED", "HEALTHY", "PASS"].includes(state)) return "bg-emerald-400/10 text-emerald-500 dark:text-emerald-300";
  if (["QUARANTINED", "FAILED", "FAIL", "CRITICAL"].includes(state)) return "bg-red-400/10 text-red-500 dark:text-red-300";
  if (["DEGRADED", "MIGRATION_REQUIRED", "WARN", "WARNING"].includes(state)) return "bg-amber-400/10 text-amber-600 dark:text-amber-200";
  return "bg-foreground/[0.06] text-foreground/60";
}

export function EnterpriseControlTowerPanel({
  supabase,
  isOwner,
}: {
  supabase: SupabaseClient | null;
  isOwner: boolean;
}) {
  const [fleet, setFleet] = useState<FleetPayload | null>(null);
  const [knowledge, setKnowledge] = useState<KnowledgeItem[]>([]);
  const [readiness, setReadiness] = useState<ReadinessPayload | null>(null);
  const [search, setSearch] = useState("");
  const [fleetFilter, setFleetFilter] = useState("ALL");
  const [knowledgeSearch, setKnowledgeSearch] = useState("");
  const [canonicalDocuments, setCanonicalDocuments] = useState(false);
  const [showHistorical, setShowHistorical] = useState(false);
  const [canonicalRelease, setCanonicalRelease] = useState<KnowledgeItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [runtimeTesting, setRuntimeTesting] = useState(false);
  const [runtimeTest, setRuntimeTest] = useState<OwnerRuntimeTest | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadFleet = useCallback(async () => {
    if (!supabase) return;
    const { data, error } = await supabase.rpc("runyi_fleet_control_tower", {
      p_search: search,
      p_status: fleetFilter,
      p_limit: 100,
      p_offset: 0,
    });
    if (error) throw error;
    setFleet((data ?? null) as FleetPayload | null);
  }, [fleetFilter, search, supabase]);

  const loadKnowledge = useCallback(async () => {
    if (!supabase) return;
    const { data, error } = await supabase.rpc("runyi_system_knowledge_search", {
      p_query: knowledgeSearch,
      p_domain: canonicalDocuments ? "CANONICAL_INDEX" : null,
      p_limit: canonicalDocuments ? 160 : 40,
    });
    if (error) throw error;
    const rows = Array.isArray(data?.results) ? data.results as KnowledgeItem[] : [];
    setKnowledge(canonicalDocuments && !showHistorical
      ? rows.filter((row) => !["HISTORICAL", "PROPOSED", "SUPERSEDED"].includes(row.content?.status ?? ""))
      : rows);
  }, [canonicalDocuments, knowledgeSearch, showHistorical, supabase]);

  const loadCanonicalRelease = useCallback(async () => {
    if (!supabase || !isOwner) return;
    const { data, error } = await supabase.rpc("runyi_system_knowledge_search", {
      p_query: "runyi.enterprise.canonical_release",
      p_domain: "ENTERPRISE_ARCHITECTURE",
      p_limit: 8,
    });
    if (error) throw error;
    const items = Array.isArray(data?.results) ? data.results as KnowledgeItem[] : [];
    setCanonicalRelease(items.find((item) => item.knowledge_key === "runyi.enterprise.canonical_release") ?? null);
  }, [isOwner, supabase]);

  const verifyOwnerRuntime = useCallback(async () => {
    if (!supabase || !isOwner || runtimeTesting) return;
    setRuntimeTesting(true);
    setRuntimeTest(null);
    const checkedAt = new Date().toISOString();
    try {
      const { data: identity, error: identityError } = await supabase.auth.getUser();
      if (identityError || !identity.user) throw new Error("Sign in with the authorized RUNYI Owner session.");
      const expectedCommit = canonicalRelease?.content?.canonical_commit ?? "";
      const expectedVersion = canonicalRelease?.content?.current_version ?? "";
      if (!/^[0-9a-f]{40}$/.test(expectedCommit) || !expectedVersion) {
        throw new Error("Cannot independently resolve the current Git release from Owner System Knowledge.");
      }
      const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
        body: { operation: "enterprise_infrastructure_overview", limit: 1 },
      });
      if (error || data?.error) {
        throw new Error(String(data?.error ?? error?.message ?? "Live Owner Control Tower request failed."));
      }
      const snapshot = data?.value_snapshot ?? {};
      const observedCommit = String(snapshot.canonical_commit ?? "UNKNOWN");
      const ownerScope = String(data?.access?.role ?? "UNKNOWN");
      const summaryCount = data?.summary && typeof data.summary === "object"
        ? Object.keys(data.summary).length : 0;
      const businessOk = data?.ok === true
        && data?.architecture_version === expectedVersion
        && snapshot.architecture_version === expectedVersion
        && snapshot.tenant_scope === "OWNER_ROOT"
        && data?.value_authorities?.architecture === "GITHUB_CANONICAL_RELEASE"
        && data?.value_authorities?.identity_authorization === "OWNER_ROOT"
        && ownerScope === "OWNER_ADMIN"
        && summaryCount > 0;
      const commitState = observedCommit === expectedCommit
        ? "VERIFIED" : observedCommit === "UNKNOWN" || !observedCommit
          ? "UNKNOWN" : "DRIFT";
      setRuntimeTest({
        checkedAt,
        state: !businessOk || commitState === "DRIFT" ? "FAILED"
          : commitState === "UNKNOWN" ? "PARTIAL" : "VERIFIED",
        businessCheck: businessOk ? "PASS" : "FAIL",
        runtimeCommitCheck: commitState,
        architecture: String(data?.architecture_version ?? "UNKNOWN"),
        expectedCommit,
        observedCommit,
        ownerScope,
        summaryCount,
        message: !businessOk ? "Runtime response did not pass Owner scope, authority, version and summary contract checks."
          : commitState === "UNKNOWN" ? "Owner-authorized business request passed; Edge runtime commit environment is not yet reported."
          : commitState === "DRIFT" ? "The live Edge runtime commit differs from current Git; do not mark synchronized."
          : "Owner-authorized Control Tower response and runtime source provenance both match current Git.",
      });
    } catch (error) {
      setRuntimeTest({
        checkedAt, state: "FAILED", businessCheck: "FAIL", runtimeCommitCheck: "UNKNOWN",
        architecture: "UNKNOWN", expectedCommit: canonicalRelease?.content?.canonical_commit ?? "UNKNOWN",
        observedCommit: "UNKNOWN", ownerScope: "UNKNOWN", summaryCount: 0,
        message: error instanceof Error ? error.message : "Live Owner Control Tower verification failed.",
      });
    } finally {
      setRuntimeTesting(false);
    }
  }, [canonicalRelease, isOwner, runtimeTesting, supabase]);

  const loadReadiness = useCallback(async () => {
    if (!supabase || !isOwner) return;
    const { data, error } = await supabase.rpc("runyi_enterprise_readiness");
    if (error) throw error;
    setReadiness((data ?? null) as ReadinessPayload | null);
  }, [isOwner, supabase]);

  const refresh = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setNotice(null);
    try {
      await Promise.all([loadFleet(), loadKnowledge(), loadReadiness(), loadCanonicalRelease()]);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not load Enterprise Control Tower.");
    } finally {
      setLoading(false);
    }
  }, [loadCanonicalRelease, loadFleet, loadKnowledge, loadReadiness, supabase]);

  useEffect(() => {
    if (!supabase || !isOwner) return;
    const timer = window.setInterval(() => {
      void loadCanonicalRelease().catch(() => setCanonicalRelease(null));
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [isOwner, loadCanonicalRelease, supabase]);

  useEffect(() => {
    const timer = window.setTimeout(() => void refresh(), 100);
    return () => window.clearTimeout(timer);
  }, [refresh]);

  const summary = fleet?.summary ?? {};
  const tenants = useMemo(() => Array.isArray(fleet?.tenants) ? fleet!.tenants! : [], [fleet]);

  return (
    <Card className="min-w-0 overflow-hidden border-0 bg-transparent py-0 shadow-none">
      <CardContent className="min-w-0 space-y-4 p-0">
        <div className="flex items-start gap-2.5">
          <div className="grid size-6 shrink-0 place-items-center rounded-md bg-primary/[0.08] text-primary">
            <ServerCog className="size-3" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Enterprise Control Tower</p>
            <p className="mt-1 text-xs leading-5 text-foreground/42">
              Fleet health, canonical system knowledge and enterprise-readiness evidence from the RUNYI control plane.
            </p>
          </div>
          <Button variant="ghost" size="icon" className="size-8" onClick={() => void refresh()} disabled={loading}>
            {loading ? <LoaderCircle className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            ["Tenants", summary.total ?? 0],
            ["Verified", summary.verified ?? 0],
            ["Degraded", summary.degraded ?? 0],
            ["Quarantined", summary.quarantined ?? 0],
          ].map(([label, value]) => (
            <div key={String(label)} className="rounded-xl bg-foreground/[0.025] px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-wider text-foreground/35">{label}</p>
              <p className="mt-1 text-lg font-semibold">{String(value)}</p>
            </div>
          ))}
        </div>

        {isOwner && <EnterpriseFabricPanel supabase={supabase} isOwner={isOwner} />}

        <div className="rounded-2xl bg-foreground/[0.022] p-3">
          <div className="mb-3 flex items-center gap-2">
            <Activity className="size-3.5 text-primary" />
            <p className="text-xs font-semibold">Fleet Operations / Health</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_170px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-foreground/30" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search email, project, lifecycle, schema or error"
                className="h-9 border-0 bg-background/70 pl-9 text-xs shadow-none"
              />
            </div>
            <Select value={fleetFilter} onValueChange={setFleetFilter}>
              <SelectTrigger className="h-9 border-0 bg-background/70 text-xs shadow-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(fleet?.filters ?? ["ALL","ACTIVE","VERIFIED","MIGRATION_REQUIRED","DEGRADED","QUARANTINED","FAILED"]).map((value) => (
                  <SelectItem key={value} value={value}>{value.replaceAll("_", " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-3 max-h-72 space-y-1.5 overflow-y-auto">
            {tenants.length === 0 && <p className="py-6 text-center text-xs text-foreground/35">No matching tenants.</p>}
            {tenants.map((tenant) => (
              <div key={tenant.project_ref ?? tenant.member_email} className="rounded-xl bg-background/55 px-3 py-2.5">
                <div className="flex min-w-0 items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-xs font-semibold">{tenant.member_email ?? tenant.display_name ?? "Tenant"}</p>
                    <p className="mt-0.5 truncate font-mono text-[10px] text-foreground/35">{tenant.project_ref ?? "No project ref"}</p>
                  </div>
                  <Badge className={statusTone(tenant.lifecycle_state)}>{tenant.lifecycle_state ?? "PROJECT_REGISTERED"}</Badge>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] text-foreground/45">
                  <span>Verified: {tenant.verified_schema_version ?? "—"}</span>
                  <span>•</span>
                  <span>Health: {tenant.health_status ?? "unknown"}</span>
                  <span>•</span>
                  <span>Drift: {tenant.active_drift_count ?? 0}</span>
                  <span>•</span>
                  <span>Security: {tenant.active_security_count ?? 0}</span>
                </div>
                {tenant.last_error && <p className="mt-1.5 line-clamp-2 text-[10px] text-red-500/80">{tenant.last_error}</p>}
              </div>
            ))}
          </div>
        </div>

        {isOwner && (
          <div className="rounded-2xl bg-foreground/[0.022] p-3" aria-label="Recorded architecture release evidence">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold">Architecture Release &amp; Runtime Evidence</p>
              <Badge className={statusTone(canonicalRelease ? "VERIFIED" : "UNKNOWN")}>
                {canonicalRelease ? "RECORDED" : "UNKNOWN"}
              </Badge>
            </div>
            <p className="mt-2 text-[11px] text-foreground/55">
              Git architecture {canonicalRelease?.content?.current_version ?? "UNKNOWN"} · Cloudflare web {canonicalRelease?.content?.web_release ?? "UNKNOWN"} · Supabase Control Tower {canonicalRelease?.content?.control_tower_edge_release ?? "UNKNOWN"}
            </p>
            <p className="mt-1 text-[10px] text-foreground/45">
              Edge function v{canonicalRelease?.content?.edge_function_version ?? "?"} · {canonicalRelease?.content?.control_tower_edge_state ?? "UNVERIFIED"} · runtime commit environment {canonicalRelease?.content?.runtime_env_canonical_commit ?? "UNKNOWN"}.
            </p>
            <p className="mt-1 text-[10px] text-foreground/45">
              Recorded authenticated test: {canonicalRelease?.content?.authenticated_business_smoke_test ?? "UNKNOWN"}. These are stored observations, not a fresh live provider attestation.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Button type="button" size="sm" disabled={!supabase || runtimeTesting} onClick={() => void verifyOwnerRuntime()}>
                {runtimeTesting ? "Checking live…" : "Verify live Owner Control Tower"}
              </Button>
              <span className="text-[10px] text-foreground/45">Uses this signed-in Owner session; no credentials are collected or stored.</span>
            </div>
            {runtimeTest && (
              <div className="mt-2 rounded-xl border border-foreground/10 bg-background/55 p-2.5" role="status">
                <div className="flex items-center gap-2">
                  <Badge className={statusTone(runtimeTest.state === "VERIFIED" ? "VERIFIED" : runtimeTest.state === "PARTIAL" ? "WARN" : "FAILED")}>
                    {runtimeTest.state}
                  </Badge>
                  <span className="text-[10px] text-foreground/55">Owner business {runtimeTest.businessCheck} · runtime commit {runtimeTest.runtimeCommitCheck}</span>
                </div>
                <p className="mt-1 text-[11px] leading-4 text-foreground/55">{runtimeTest.message}</p>
                <p className="mt-1 break-all font-mono text-[9px] text-foreground/40">v{runtimeTest.architecture} · role {runtimeTest.ownerScope} · summary keys {runtimeTest.summaryCount} · runtime {runtimeTest.observedCommit} · Git {runtimeTest.expectedCommit}</p>
                <p className="text-[9px] text-foreground/35">Observed {new Date(runtimeTest.checkedAt).toLocaleString()} · browser-only result until server audit confirms.</p>
              </div>
            )}
          </div>
        )}

        <div className="rounded-2xl bg-foreground/[0.022] p-3">
          <div className="mb-3 flex items-center gap-2">
            <BookOpen className="size-3.5 text-primary" />
            <p className="text-xs font-semibold">Canonical System Knowledge</p>
          </div>
          <div className="mb-2 flex flex-wrap gap-1.5">
            <Button type="button" size="sm" variant={canonicalDocuments ? "outline" : "secondary"} onClick={() => setCanonicalDocuments(false)}>Knowledge</Button>
            <Button type="button" size="sm" variant={canonicalDocuments ? "secondary" : "outline"} onClick={() => setCanonicalDocuments(true)}>Git documents</Button>
            {canonicalDocuments && <Button type="button" size="sm" variant="ghost" onClick={() => setShowHistorical((value) => !value)}>{showHistorical ? "Hide historical" : "Show historical"}</Button>}
          </div>
          {canonicalDocuments && <p className="mb-2 text-[10px] leading-4 text-foreground/45">Metadata index only · read the full pinned Git source before modifying RUNYI. Historical entries are context, never current instructions.</p>}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-foreground/30" />
            <Input
              value={knowledgeSearch}
              onChange={(event) => setKnowledgeSearch(event.target.value)}
              placeholder="Search architecture, security, deployment, integrations…"
              className="h-9 border-0 bg-background/70 pl-9 text-xs shadow-none"
            />
          </div>
          <div className="mt-3 max-h-64 space-y-1.5 overflow-y-auto">
            {knowledge.map((item) => (
              <div key={item.knowledge_key} className="rounded-xl bg-background/55 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary/[0.08] text-primary">{item.domain}</Badge>
                  {item.authoritative && <ShieldCheck className="size-3 text-emerald-500" />}
                </div>
                <p className="mt-1.5 text-xs font-semibold">{item.title}</p>
                <p className="mt-1 text-[11px] leading-4 text-foreground/45">{item.summary}</p>
                {canonicalDocuments && item.content?.git_url && (
                  <a href={item.content.git_url} target="_blank" rel="noopener noreferrer" className="mt-1.5 block break-all text-[10px] font-medium text-primary underline underline-offset-2">Read pinned Git document</a>
                )}
                {canonicalDocuments && item.content?.status && (
                  <p className="mt-1 text-[10px] text-foreground/40">{item.content.status} · {item.content.domain ?? "unclassified"}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {isOwner && <ProviderControlPanel supabase={supabase} isOwner={isOwner} />}

        {isOwner && readiness && (
          <div className="rounded-2xl bg-foreground/[0.022] p-3">
            <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-[1_1_12rem]">
                <p className="text-xs font-semibold">Enterprise Readiness Gate</p>
                <p className="mt-1 text-[11px] text-foreground/42">
                  Required checks must pass before large-scale fleet expansion.
                </p>
              </div>
              <Badge className={`${statusTone(readiness.ready ? "PASS" : "WARN")} max-w-full whitespace-normal break-words`}>
                {readiness.ready ? "READY" : "NOT READY"}
              </Badge>
            </div>
            <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
              {(readiness.checks ?? []).map((check) => (
                <ResponsiveStatusRow key={check.check_key} className="bg-background/55"
                  title={check.description}
                  category={check.category}
                  status={check.status.replaceAll("_", " ")}
                  statusClassName={statusTone(check.status)} />
              ))}
            </div>
          </div>
        )}

        {notice && <p className="rounded-xl bg-amber-300/[0.08] px-3 py-2 text-[11px] text-amber-700 dark:text-amber-100">{notice}</p>}
      </CardContent>
    </Card>
  );
}
